import { useMsal } from '@azure/msal-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FilterSVG from 'src/assets/images/icons/FilterSVG';
import { formatDateForFileName } from 'src/utils/utils';
import DownloadSVG from '../../assets/images/icons/DownloadSVG';
import { BRIGHT_INSIGHTS_FEEDBACK_URL, BRIGHT_INSIGHTS_URL } from '../../utils/apiUrls';
import { getAccessToken } from '../../utils/token-service';
import BrightInsightsFeedback from './edit-tab';
import FilterSidebar from './fiter-sidebar';

interface ApiResponse {
	id: number;
	created_at: string;
	created_by?: string | null;
	llm_answer: string;
	llm_answered: boolean;
	remarks: string;
	updated_by?: string;
	updated_date?: string;
	source_document: string;
	search_key: string;
	likes: number;
	dislikes: number;
	status: string;
}

const BrightInsights: React.FC = () => {
	const { instance } = useMsal();
	const gridRef = useRef<AgGridReact>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [brightInsights, setBrightInsights] = useState<ApiResponse[]>([]);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [selectedRowData, setSelectedRowData] = useState<ApiResponse | null>(null);
	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');
	const [startTime, setStartTime] = useState<string>('');
	const [endTime, setEndTime] = useState<string>('');
	const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

	const accounts = instance.getAllAccounts();
	const loggedInUserName = accounts && accounts.length > 0 ? accounts[0].name : '';

	const toggleSidebar = () => {
		setIsSidebarOpen((prev) => !prev);
	};

	const fetchBrightInsight = async () => {
		try {
			setLoading(true);
			const accessToken = await getAccessToken();

			const requestBody: any = {};
			if (startDate && startTime) {
				requestBody.from_date = `${startDate}T${startTime}:00`;
				requestBody.user_name = loggedInUserName;
			} else if (startDate) {
				requestBody.from_date = `${startDate}T00:00:00`;
			}
			if (endDate && endTime) {
				requestBody.to_date = `${endDate}T${endTime}:00`;
			} else if (endDate) {
				requestBody.to_date = `${endDate}T23:59:59`;
			}

			const response = await fetch(BRIGHT_INSIGHTS_URL, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(`HTTP error! Status: ${response.status}, Details: ${JSON.stringify(errorData)}`);
			}

			const data = await response.json();
			const parsedData = data.map((item: any) => {
				let parsedApi;
				try {
					parsedApi = JSON.parse(item.api_response);
				} catch (e) {
					console.warn('Failed to parse api_response:', item.api_response);
					parsedApi = {};
				}

				return {
					...item,
					llm_answer: parsedApi.response || item.llm_answer || '',
					source_document: parsedApi.citation || item.source_document || '',
					likes: typeof parsedApi.likes === 'number' ? parsedApi.likes : 0,
					dislikes: typeof parsedApi.dislikes === 'number' ? parsedApi.dislikes : 0,
					llm_answered:
						typeof parsedApi.llm_answered === 'boolean' ? parsedApi.llm_answered : item.llm_answered || false,
					// status: item.status || 'new',
				};
			});
			const sortedData = parsedData.sort((a: ApiResponse, b: ApiResponse) => {
				const dateA = new Date(b.updated_date || b.created_at).getTime();
				const dateB = new Date(a.updated_date || a.created_at).getTime();
				return dateA - dateB;
			});
			setBrightInsights(sortedData);
			console.log('bright insights data ==>', data);
		} catch (err) {
			console.error('Error fetching Bright Insights:', err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchBrightInsight();
	}, []);

	const applyFilters = () => {
		fetchBrightInsight();
	};

	const handleEditClick = (params: any) => {
		const { id, remarks, search_key, llm_answer, updated_by, status } = params.data;
		setSelectedRowData({ id, remarks, search_key, llm_answer, updated_by, status } as ApiResponse);
		setIsModalOpen(true);
	};

	const handleSave = async (updatedData: ApiResponse, loggedInUserName: string) => {
		try {
			const accessToken = await getAccessToken();
			const updatedDataWithUser = {
				...updatedData,
				updated_by: loggedInUserName,
			};

			const response = await fetch(`${BRIGHT_INSIGHTS_FEEDBACK_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
				},
				body: JSON.stringify(updatedDataWithUser),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const result = await response.json();
			console.log('Updated response:', result);
			handleModalClose();
			fetchBrightInsight();
		} catch (error) {
			console.error('Error updating data:', error);
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setSelectedRowData(null);
	};

	const SourceDocumentRenderer = (params: any) => {
		const htmlString = params.value;
		const styledHtml = htmlString.replace(/<a /g, '<a style="color: blue; text-decoration: underline;" ');
		return <div dangerouslySetInnerHTML={{ __html: styledHtml }} />;
	};

	const colDefs = [
		{ field: 'search_key', headerName: 'Search Key', width: 170 },
		{ field: 'llm_answer', headerName: 'LLM Answer', width: 250, editable: true },
		{
			field: 'source_document',
			headerName: 'Source document',
			cellRenderer: SourceDocumentRenderer,
		},
		{
			field: 'llm_answered',
			headerName: 'LLM Answered',
			width: 150,
			cellRenderer: (params: any) => (
				<div className="text-center">
					<p
						className={`px-3 inline-block leading-6 rounded-md h-6 text-sm ${params.value ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
							}`}
					>
						{params.value ? 'Yes' : 'No'}
					</p>
				</div>
			),
		},
		{
			field: 'likes',
			headerName: 'Likes',
			width: 100,
			cellRenderer: (params: any) => <span>{params.value !== undefined ? params.value : '-'}</span>,
		},
		{ field: 'dislikes', headerName: 'Dislikes', width: 100 },
		{ field: 'remarks', headerName: 'Remarks', width: 170 },
		{ field: 'created_by', headerName: 'User Name' },
		{
			field: 'action',
			headerName: 'Actions',
			cellRenderer: (params: any) => (
				<button
					className="flex items-center justify-between px-2 py-1 leading-4 bg-sky-600 shadow-sm rounded text-white"
					onClick={() => handleEditClick(params)}
				>
					Edit
				</button>
			),
			width: 170,
			editable: false,
		},
		{
			field: 'status',
			headerName: 'Status',
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: ['New', 'Approved', 'Denied', 'Pending LLM changes', 'Pending content changes', 'Completed'],
			},
			width: 200,
		},
	];

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapText: true,
			autoHeight: true,
			cellStyle: { lineHeight: '20px', padding: '8px 14px', wordWrap: 'break-word' },
		}),
		[]
	);

	const onBtnExport = useCallback(() => {
		const fileName = `bright_insights_${formatDateForFileName()}.csv`;

		const allColumnIds: string[] = [];
		gridRef.current?.api.getColumns()?.forEach((col) => {
			const colId = col.getColId();
			if (colId !== 'action') {
				allColumnIds.push(colId);
			}
		});

		gridRef.current?.api.exportDataAsCsv({
			fileName,
			columnKeys: allColumnIds,
		});
	}, []);

	return (
		<div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
			<div className="flex justify-between items-center">
				<h1 className="text-lg font-semibold leading-6 mb-0 text-gray-900">Bright Insights</h1>
				<div className='flex gap-2'>
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={onBtnExport}
					>
						Export CSV <DownloadSVG />
					</button>
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={toggleSidebar}
					>
						Filter <FilterSVG />
					</button>
				</div>
			</div>
			<div className="flex mt-2 pt-2 border-t">
				<div className="flex-1">
					{loading ? (
						<div className="text-sm">Loading...</div>
					) : (
						<div className="ag-theme-alpine" style={{ height: '70vh' }}>
							<AgGridReact
								ref={gridRef}
								suppressExcelExport={true}
								pagination={true}
								paginationPageSize={20}
								defaultColDef={defaultColDef}
								rowData={brightInsights}
								columnDefs={colDefs}
								rowSelection={'multiple'}
								editType={'fullRow'}
							/>
						</div>
					)}
				</div>
			</div>

			{isModalOpen && selectedRowData && (
				<BrightInsightsFeedback
					key={selectedRowData.id}
					isOpen={isModalOpen}
					onClose={handleModalClose}
					onSave={(updatedData) => handleSave(updatedData, loggedInUserName!)}
					initialData={selectedRowData}
				/>
			)}
			<FilterSidebar
				isOpen={isSidebarOpen}
				toggleSidebar={toggleSidebar}
				setStartDate={setStartDate}
				setEndDate={setEndDate}
				setStartTime={setStartTime}
				setEndTime={setEndTime}
				applyFilters={applyFilters}
			/>
		</div>
	);
};

export default BrightInsights;
